import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Settings, Check } from 'lucide-react';

const STORAGE_KEY = 'evolvebpm.cookieConsent.v1';

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

function readStoredConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.necessary !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(prefs) {
  const payload = { ...prefs, timestamp: new Date().toISOString() };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  // Fire a custom event so analytics scripts can gate themselves off this
  try {
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: payload }));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const existing = readStoredConsent();
    if (!existing) {
      // Show banner after intro loader ends (~5.5s on /, immediately elsewhere)
      const isHome = typeof window !== 'undefined' && window.location.pathname === '/';
      const delay = isHome ? 5800 : 500;
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
    setPrefs(existing);
    return undefined;
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    writeConsent(next);
    setPrefs(next);
    setVisible(false);
  };
  const rejectAll = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    writeConsent(next);
    setPrefs(next);
    setVisible(false);
  };
  const saveSelection = () => {
    writeConsent(prefs);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="max-w-5xl mx-auto pointer-events-auto">
        <div className="relative bg-[#0F1C40] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          <div
            className="absolute -top-16 -right-16 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.35), transparent 70%)' }}
          />

          {!showSettings ? (
            <div className="relative p-5 lg:p-6 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#FFB020]/20 border border-[#FFB020]/40 flex items-center justify-center shrink-0">
                  <Cookie className="w-4 h-4 text-[#FFB020]" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-1">
                    Cookies &amp; privacy
                  </div>
                  <p className="text-[13px] text-white/80 leading-snug">
                    We use cookies to run this site, remember your preferences, and — with your consent —
                    measure how you use it. You&apos;re in control.{' '}
                    <Link to="/privacy" className="text-[#4ECDC4] underline">
                      Read our Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  data-testid="cookie-customize"
                  onClick={() => setShowSettings(true)}
                  className="inline-flex items-center gap-1.5 text-[12px] text-white/70 hover:text-[#4ECDC4] px-3 py-2 rounded-full border border-white/10 hover:border-[#4ECDC4]"
                >
                  <Settings className="w-3.5 h-3.5" /> Customize
                </button>
                <button
                  data-testid="cookie-reject"
                  onClick={rejectAll}
                  className="text-[12px] text-white/70 hover:text-[#F1EDE4] px-4 py-2 rounded-full border border-white/10 hover:border-white/40"
                >
                  Reject all
                </button>
                <button
                  data-testid="cookie-accept"
                  onClick={acceptAll}
                  className="text-[12px] font-semibold text-[#0F1C40] bg-[#4ECDC4] hover:bg-[#FFB020] px-4 py-2 rounded-full"
                >
                  Accept all
                </button>
              </div>
            </div>
          ) : (
            <div className="relative p-5 lg:p-6" data-testid="cookie-settings">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-1">
                    Cookie preferences
                  </div>
                  <h3 className="text-lg font-serif italic text-[#F1EDE4]">Choose what to allow.</h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-white/50 hover:text-white/90"
                  aria-label="Close preferences"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2.5">
                {[
                  {
                    key: 'necessary',
                    title: 'Strictly necessary',
                    desc: 'Required for the site to work — session, security, load-balancing. Always on.',
                    locked: true,
                  },
                  {
                    key: 'analytics',
                    title: 'Analytics',
                    desc: 'Anonymous usage metrics that help us improve the site.',
                    locked: false,
                  },
                  {
                    key: 'marketing',
                    title: 'Marketing',
                    desc: 'Tracks your interest in Predictor.com and demand-gen content across sessions.',
                    locked: false,
                  },
                ].map((c) => (
                  <label
                    key={c.key}
                    className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-lg p-3 cursor-pointer hover:border-white/20"
                  >
                    <input
                      type="checkbox"
                      data-testid={`cookie-toggle-${c.key}`}
                      checked={prefs[c.key]}
                      disabled={c.locked}
                      onChange={(e) => setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-[#4ECDC4] shrink-0 disabled:opacity-60"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] text-[#F1EDE4] font-medium">{c.title}</span>
                        {c.locked && (
                          <span className="text-[9px] font-mono uppercase tracking-widest text-[#4ECDC4] border border-[#4ECDC4]/40 px-1.5 py-0.5 rounded-full">
                            required
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-white/60 leading-relaxed mt-0.5">{c.desc}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
                <button
                  onClick={rejectAll}
                  className="text-[12px] text-white/70 hover:text-[#F1EDE4] px-4 py-2 rounded-full border border-white/10"
                >
                  Reject all
                </button>
                <button
                  data-testid="cookie-save"
                  onClick={saveSelection}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0F1C40] bg-[#4ECDC4] hover:bg-[#FFB020] px-4 py-2 rounded-full"
                >
                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Save preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
